import {createApi} from "@reduxjs/toolkit/query/react";
import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {appendFormData} from "@/helpers/form";

export const eventsApi = createApi({
    reducerPath: "eventsApi",
    tagTypes: ["events"],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: (build) => ({
        getEvents: build.query({
            query: (data) => ({
                url: api.events.getEvents(),
                method: "GET",
                params: data,
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id, queryArgs.date];
            },
            merge: (currentCache, newItems, {arg}) => {
                if (!arg.skip) {
                    return newItems;
                }
                currentCache.push(
                    ...newItems.filter(
                        (newItem) => !currentCache.some((item) => item.id === newItem.id)
                    )
                );
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ["events"],
        }),
        createEvent: build.mutation({
            query: (data) => {
                const formData = new FormData()
                appendFormData(formData, data)

                return {
                    url: api.events.createEvent(),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    body: formData,
                    formData: true
                };
            },
            async onQueryStarted(patch, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled
                    dispatch(
                        eventsApi.util.updateQueryData('getEvents', patch, (draft) => {
                            draft.unshift(data)
                        })
                    )
                } catch (error) {
                    console.error('Failed to update cache:', error)
                }
            },
        }),
        updateEvent: build.mutation({
            query: ({ id, ...data }) => {
                const formData = new FormData();

                data = Object.fromEntries(
                    Object.entries(data).filter(([_, value]) => value !== undefined)
                )

                appendFormData(formData, data)

                formData.append('_method', 'PUT')

                return {
                    url: api.events.updateEvent(id),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data;'
                    },
                    body: formData,
                    formData: true
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        eventsApi.util.updateQueryData('getEvents', {location_id: patch.location_id}, (draft) => {
                            const itemIndex = draft?.findIndex(item => item.id === patch.id);
                            if (itemIndex !== -1) {
                                draft[itemIndex] = data;
                            }
                        })
                    );
                } catch (e) {
                    console.error('Update failed:', e);
                }
            },
        }),
        deleteEvent: build.mutation({
            query: ({id}) => ({
                url: api.events.deleteEvent(id),
                method: 'DELETE',
            }),
            async onQueryStarted(patch, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    dispatch(
                        eventsApi.util.updateQueryData('getEvents', patch, (draft) => {
                            const index = draft.findIndex((event) => event.id === patch.id);
                            if (index !== -1) {
                                draft.splice(index, 1)
                            } else {
                                console.warn('Event not found in cache')
                            }
                        })
                    );
                } catch (e) {
                    console.error('Delete failed:', e)
                }
            },
        }),
    }),
});

export const { useDeleteEventMutation } = eventsApi;
